import * as React from 'react';
import { useEffect } from 'react';

import { useAdaptiveLearningContext } from '@coursera-web/course-frame';
import type { LocationDescriptorObject } from 'history';

import connectToStores from 'js/lib/connectToStores';
import { useLocation, useNavigate, useParams } from 'js/lib/useRouter';

import { getAllSkippableNextStepsExcept } from 'bundles/course-home/page-course-week/components/next-step-card/constants';
import type { SkippableNextStep } from 'bundles/course-home/page-course-week/components/next-step-card/constants';
import { useCourseNavigationContext } from 'bundles/course-v2/providers/CourseNavigationProvider';
import CourseNextStepProvider from 'bundles/course-v2/providers/CourseNextStepProvider';
import { useIsWeekToModuleEnabledForCourse } from 'bundles/custom-labels/utils/featureFlags';

type Props = {
  courseId: string;
};

const Redirect = ({ to }: { to: string | LocationDescriptorObject }) => {
  const navigate = useNavigate();

  useEffect(() => {
    navigate(to);
  }, [to, navigate]);

  return null;
};

// We are only interested in the Course Material Next Step to determine what
// module url to route them to
const skippableStepsToExclude = new Set<SkippableNextStep>(['CourseMaterialNextStep']);

export const CourseHomeRefreshRedirect = ({ courseId }: Props) => {
  const params = useParams();
  const location = useLocation();
  const courseSlug = params.courseSlug;
  // I see a comment below saying the connect to store call that retrieves the courseId is always undefined, this may be causing a failed query everytime the page redirects
  // since its entering an undefined courseId so testing that theory out
  const isWeekToModuleEnabledForCourse = useIsWeekToModuleEnabledForCourse(courseId);

  const { currentModule, loading: courseNavigationLoading } = useCourseNavigationContext();
  const currentQueryParams = location.query;

  const stepsToSkip = getAllSkippableNextStepsExcept(skippableStepsToExclude);

  const { show: adaptiveEnabled } = useAdaptiveLearningContext();
  if (adaptiveEnabled) {
    return null;
  }

  // We use the CourseNavigation currentWeek (currentModule) value as a backup in case
  // the current learner does not have a courseMaterialNextStep
  return (
    // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    <CourseNextStepProvider courseSlug={courseSlug} stepsToSkip={stepsToSkip}>
      {({ courseNextStep, loading: courseNextStepLoading }) => {
        const nextStep = courseNextStep?.nextStep;

        let currentWeekNumber = 1;

        // don't redirect until we have data from both providers
        if (courseNavigationLoading || courseNextStepLoading) return null;

        if (nextStep?.typeName === 'courseMaterialNextStep') {
          currentWeekNumber = nextStep?.definition.currentWeekNumberByProgress;
        } else if (currentModule) {
          currentWeekNumber = currentModule;
        }

        if (isWeekToModuleEnabledForCourse) {
          return (
            <Redirect
              to={{
                pathname: `/learn/${courseSlug}/home/module/${currentWeekNumber}`,
                query: currentQueryParams,
              }}
            />
          );
        } else {
          return (
            <Redirect
              to={{
                pathname: `/learn/${courseSlug}/home/week/${currentWeekNumber}`,
                query: currentQueryParams,
              }}
            />
          );
        }
      }}
    </CourseNextStepProvider>
  );
};

export default connectToStores<Props, {}>(['CourseStore'], (CourseStore) => {
  return {
    /**
     * TODO: this always return undefined should it just be deleted?
     */
    courseId: CourseStore.getCourseId(),
  };
})(CourseHomeRefreshRedirect);
